import { useEffect, useMemo, useRef, useState } from "react";
import { formatPhone } from "@/lib/utils";
import { Link, useSearchParams } from "react-router-dom";
import {
  ArrowRight,
  Clock3,
  Lock,
  LockOpen,
  Mail,
  Phone,
  Search,
  Unlock,
  User,
} from "lucide-react";

import { api } from "@/lib/api";
import { Pagination } from "@/components/ui/pagination";
import {
  EmptyPanel,
  MetricTile,
  PageHero,
  PagePanel,
  PageShell,
  PanelHeading,
} from "@/components/interior-page";
import Loader from "@/components/ui/loader";

const PAGE_SIZE = 20;

interface ActivityItem {
  id: string;
  activity_type: "lock" | "unlock";
  buyer_id: string;
  buyer_name: string | null;
  buyer_email: string | null;
  buyer_phone: string | null;
  tier_id: number | null;
  tier_name: string | null;
  listing_id: string | null;
  listing_title: string | null;
  detail: string | null; // unlock: free|paid ; lock: active|expired|released
  cost_amount: number | null;
  locked_until: string | null;
  released_at: string | null;
  event_at: string | null;
}

interface Summary {
  total_unlocks: number;
  paid_unlocks: number;
  free_unlocks: number;
  total_locks: number;
  active_locks: number;
}

interface ApiResponse {
  total_elements: number;
  summary: Summary;
  data: ActivityItem[];
}

interface TierOption {
  id: number;
  name: string;
}

type TypeFilter = "" | "lock" | "unlock";

const EMPTY_SUMMARY: Summary = {
  total_unlocks: 0,
  paid_unlocks: 0,
  free_unlocks: 0,
  total_locks: 0,
  active_locks: 0,
};

function DetailBadge({ item }: { item: ActivityItem }) {
  if (item.activity_type === "unlock") {
    const paid = item.detail === "paid";
    return (
      <span
        className={`inline-flex items-center gap-1 rounded-full px-2.5 py-1 text-[0.62rem] font-bold uppercase tracking-[0.15em] ${
          paid ? "bg-amber-100 text-amber-700" : "bg-emerald-100 text-emerald-700"
        }`}
      >
        <Unlock className="h-3 w-3" />
        {paid ? `Paid${item.cost_amount != null ? ` · $${item.cost_amount}` : ""}` : "Free"}
      </span>
    );
  }
  // lock
  const tone =
    item.detail === "active"
      ? "bg-primary/10 text-primary"
      : item.detail === "released"
        ? "bg-slate-200 text-slate-600"
        : "bg-rose-100 text-rose-700"; // expired
  return (
    <span className={`inline-flex items-center gap-1 rounded-full px-2.5 py-1 text-[0.62rem] font-bold uppercase tracking-[0.15em] ${tone}`}>
      <Lock className="h-3 w-3" />
      {item.detail ?? "lock"}
    </span>
  );
}

export default function AdminBuyerActivityPage() {
  const [items, setItems] = useState<ActivityItem[]>([]);
  const [summary, setSummary] = useState<Summary>(EMPTY_SUMMARY);
  const [loading, setLoading] = useState(true);
  // Page lives in the URL (?page=N, 1-based) so returning from a listing detail
  // via "Back to listings" restores the exact page the admin was on.
  const [searchParams, setSearchParams] = useSearchParams();
  const page = Math.max(1, Number(searchParams.get("page") ?? "1") || 1);
  const setPage = (next: number) => {
    setSearchParams(
      (prev) => {
        const p = new URLSearchParams(prev);
        if (next > 1) p.set("page", String(next));
        else p.delete("page");
        return p;
      },
      { replace: true },
    );
  };
  const [total, setTotal] = useState(0);

  const [typeFilter, setTypeFilter] = useState<TypeFilter>("");
  const [tierFilter, setTierFilter] = useState<number | "">("");
  const [searchInput, setSearchInput] = useState("");
  const [search, setSearch] = useState("");
  const [tiers, setTiers] = useState<TierOption[]>([]);

  useEffect(() => {
    api.get<TierOption[]>("/lookups/tiers").then(setTiers).catch(() => {});
  }, []);

  function fetchActivity(targetPage = page) {
    setLoading(true);
    const params = new URLSearchParams({
      skip: String((targetPage - 1) * PAGE_SIZE),
      limit: String(PAGE_SIZE),
    });
    if (typeFilter) params.set("activity_type", typeFilter);
    if (tierFilter !== "") params.set("tier_id", String(tierFilter));
    if (search.trim()) params.set("search", search.trim());

    api
      .get<ApiResponse>(`/admin/buyer-activity?${params.toString()}`)
      .then((res) => {
        setItems(res.data);
        setTotal(res.total_elements);
        setSummary(res.summary ?? EMPTY_SUMMARY);
        setPage(targetPage);
      })
      .catch(() => {})
      .finally(() => setLoading(false));
  }

  // On first mount, fetch the page from the URL (restores position when
  // returning from a listing detail). On later filter changes, reset to page 1.
  const didInit = useRef(false);
  useEffect(() => {
    if (didInit.current) {
      fetchActivity(1);
    } else {
      didInit.current = true;
      fetchActivity(page);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [typeFilter, tierFilter, search]);

  // Passed as router state on each listing link so ListingDetail's
  // "Back to listings" returns here with the current page preserved.
  const backTo = page > 1 ? `/admin/buyer-activity?page=${page}` : "/admin/buyer-activity";

  const headline = useMemo(
    () => summary.total_unlocks + summary.total_locks,
    [summary],
  );

  return (
    <PageShell>
      <PageHero
        eyebrow="Buyer Activity"
        title="Track every listing lock and unlock across all buyer tiers."
        description="Audit which buyers unlocked or locked which listings, whether unlocks were free or paid, and which locks are still active — all in one searchable feed."
        aside={
          <>
            <div className="text-[0.72rem] font-bold uppercase tracking-[0.22em] text-primary/70">Activity volume</div>
            <div className="mt-3 text-3xl font-black tracking-tight text-foreground">{headline} total events</div>
            <p className="mt-2 text-sm leading-7 text-muted-foreground">
              {summary.total_unlocks} unlocks ({summary.paid_unlocks} paid) and {summary.total_locks} locks, with {summary.active_locks} currently active.
            </p>
          </>
        }
      />

      <section className="grid gap-4 sm:grid-cols-2 xl:grid-cols-4">
        <MetricTile label="Total Unlocks" value={summary.total_unlocks} icon={Unlock} tone="emerald" />
        <MetricTile label="Paid Unlocks" value={summary.paid_unlocks} icon={LockOpen} tone="amber" />
        <MetricTile label="Total Locks" value={summary.total_locks} icon={Lock} tone="primary" />
        <MetricTile label="Active Locks" value={summary.active_locks} icon={Clock3} tone="rose" />
      </section>

      <PagePanel>
        <PanelHeading
          title="Lock & unlock stream"
          description="Newest activity first. Filter by type, tier, or search by buyer or listing."
        />

        {/* Filters */}
        <div className="mb-4 flex flex-col gap-3 sm:flex-row sm:items-center">
          <div className="relative flex-1">
            <Search className="pointer-events-none absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-muted-foreground" />
            <input
              value={searchInput}
              onChange={(e) => setSearchInput(e.target.value)}
              onKeyDown={(e) => { if (e.key === "Enter") setSearch(searchInput); }}
              onBlur={() => setSearch(searchInput)}
              placeholder="Search buyer name, email, or listing…"
              className="w-full rounded-xl border border-input bg-white py-2 pl-9 pr-3 text-sm outline-none focus:border-primary focus:ring-2 focus:ring-primary/20"
            />
          </div>
          <select
            value={typeFilter}
            onChange={(e) => setTypeFilter(e.target.value as TypeFilter)}
            className="rounded-xl border border-input bg-white px-3 py-2 text-sm outline-none focus:border-primary focus:ring-2 focus:ring-primary/20"
          >
            <option value="">All activity</option>
            <option value="unlock">Unlocks only</option>
            <option value="lock">Locks only</option>
          </select>
          <select
            value={tierFilter}
            onChange={(e) => setTierFilter(e.target.value === "" ? "" : Number(e.target.value))}
            className="rounded-xl border border-input bg-white px-3 py-2 text-sm outline-none focus:border-primary focus:ring-2 focus:ring-primary/20"
          >
            <option value="">All tiers</option>
            {tiers.map((t) => (
              <option key={t.id} value={t.id}>{t.name}</option>
            ))}
          </select>
        </div>

        {loading ? (
          <Loader className="min-h-[40vh]" />
        ) : items.length === 0 ? (
          <EmptyPanel
            icon={Lock}
            title="No activity found"
            description="Once buyers lock or unlock listings, their activity will appear here."
          />
        ) : (
          <div className="space-y-3">
            {items.map((item) => (
              <article
                key={`${item.activity_type}-${item.id}`}
                className="rounded-[1.55rem] border border-border/60 bg-white/72 p-4 shadow-[0_1px_0_hsl(0_0%_100%/0.82)_inset] transition-all duration-200 hover:-translate-y-0.5 hover:border-primary/20 hover:bg-white"
              >
                <div className="flex flex-col gap-4 lg:flex-row lg:items-start">
                  <div
                    className={`flex h-12 w-12 shrink-0 items-center justify-center rounded-[1rem] ${
                      item.activity_type === "unlock"
                        ? "bg-emerald-100 text-emerald-700"
                        : "bg-primary/10 text-primary"
                    }`}
                  >
                    {item.activity_type === "unlock" ? <Unlock className="h-5 w-5" /> : <Lock className="h-5 w-5" />}
                  </div>

                  <div className="min-w-0 flex-1">
                    <div className="flex flex-wrap items-center gap-2">
                      <span className="inline-flex items-center gap-1.5 text-sm font-black text-foreground">
                        <User className="h-3.5 w-3.5 text-muted-foreground" />
                        {item.buyer_name || "Unknown buyer"}
                      </span>
                      <DetailBadge item={item} />
                      {item.tier_name && (
                        <span className="rounded-full bg-slate-100 px-2.5 py-1 text-[0.62rem] font-bold uppercase tracking-[0.15em] text-slate-600">
                          {item.tier_name}
                        </span>
                      )}
                    </div>

                    {item.listing_id ? (
                      <Link
                        to={`/listings/${item.listing_id}`}
                        state={{ from: backTo }}
                        className="mt-1 inline-flex items-center gap-1 text-sm font-semibold text-primary hover:text-primary-light"
                      >
                        {item.listing_title || "View listing"}
                        <ArrowRight className="h-3.5 w-3.5" />
                      </Link>
                    ) : (
                      <p className="mt-1 text-sm font-semibold text-muted-foreground">{item.listing_title || "Listing removed"}</p>
                    )}

                    <div className="mt-3 flex flex-wrap gap-3 text-xs text-muted-foreground">
                      {item.event_at && <span>{new Date(item.event_at).toLocaleString()}</span>}
                      {item.activity_type === "lock" && item.locked_until && (
                        <span className="inline-flex items-center gap-1.5">
                          <Clock3 className="h-3.5 w-3.5" />
                          {item.detail === "active" ? "Locked until " : "Was locked until "}
                          {new Date(item.locked_until).toLocaleString()}
                        </span>
                      )}
                      {item.buyer_email && (
                        <span className="inline-flex items-center gap-1.5">
                          <Mail className="h-3.5 w-3.5" />
                          {item.buyer_email}
                        </span>
                      )}
                      {item.buyer_phone && (
                        <span className="inline-flex items-center gap-1.5">
                          <Phone className="h-3.5 w-3.5" />
                          {formatPhone(item.buyer_phone)}
                        </span>
                      )}
                    </div>
                  </div>
                </div>
              </article>
            ))}
          </div>
        )}

        <Pagination
          page={page}
          totalElements={total}
          pageSize={PAGE_SIZE}
          onChange={fetchActivity}
          className="mt-6"
        />
      </PagePanel>
    </PageShell>
  );
}
